import { useEffect, useRef, useState } from 'react';
import { useAiko } from './store';

export function Aiko() {
  const { isOpen, open, close, messages, busy, ask, reset } = useAiko();
  const [text, setText] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        isOpen ? close() : open();
        setTimeout(() => inputRef.current?.focus(), 50);
      }
      if (e.key === 'Escape' && isOpen) close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, open, close]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }); }, [messages]);

  const send = () => {
    const t = text.trim();
    if (!t) return;
    setText('');
    ask(t);
  };

  return (
    <>
      {!isOpen && (
        <button
          onClick={open}
          className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-40 h-12 px-5 rounded-full bg-accent text-bg font-medium shadow-lg shadow-accent/30 hover:scale-[1.02] transition-transform"
          aria-label="Open Aiko"
        >
          ask aiko
        </button>
      )}

      {isOpen && (
        <div className="fixed inset-x-0 bottom-0 z-50 md:inset-auto md:bottom-6 md:right-6 md:w-[420px]">
          <div className="glass border-t border-line md:border md:rounded-2xl overflow-hidden flex flex-col h-[80dvh] md:h-[600px] shadow-2xl shadow-bg/60">
            <div className="flex items-center gap-2 px-4 h-12 border-b border-line">
              <div className="w-7 h-7 rounded-full bg-accent grid place-items-center text-bg font-semibold text-sm">A</div>
              <div className="font-medium">Aiko</div>
              <div className="text-xs text-muted">{busy ? 'thinking…' : 'ready'}</div>
              <div className="ml-auto flex items-center gap-1">
                <button onClick={reset} className="text-xs text-muted hover:text-ink px-2 h-7 rounded-md hover:bg-elevated">clear</button>
                <button onClick={close} className="text-muted hover:text-ink px-2 h-7 rounded-md hover:bg-elevated" aria-label="Close">×</button>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 scroll-hidden">
              {messages.length === 0 && (
                <div className="text-sm text-muted">
                  I read your fleet, orders, customers, repairs, leads and rates. Ask me anything — I'll show my work.
                </div>
              )}
              {messages.map((m) => (
                <div key={m.id}>
                  {m.role === 'user' && (
                    <div className="ml-auto max-w-[88%] bg-accent/15 text-ink rounded-2xl rounded-tr-md px-3 py-2 text-sm w-fit">{m.text}</div>
                  )}
                  {m.role === 'assistant' && (m.text || m.pending) && (
                    <div className="max-w-[88%] bg-elevated rounded-2xl rounded-tl-md px-3 py-2 text-sm">
                      {m.text}
                      {m.pending && !m.text && <span className="inline-block w-1 h-4 bg-ink/60 ml-0.5 animate-pulse align-middle" />}
                      {m.error && <span className="block mt-1 text-danger text-xs">{m.error}</span>}
                    </div>
                  )}
                  {m.role === 'tool' && (
                    <div className="text-xs text-muted font-mono pl-2 border-l-2 border-line">
                      {m.pending ? '· calling' : '· called'} {m.toolName}({m.toolArgs ? JSON.stringify(m.toolArgs).slice(0, 80) : ''}{m.toolArgs && JSON.stringify(m.toolArgs).length > 80 ? '…' : ''})
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="border-t border-line p-3">
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="show me 5T forklifts available next week"
                  rows={1}
                  className="flex-1 resize-none bg-elevated rounded-xl px-3 py-2 text-sm border border-line focus:border-accent focus:outline-none max-h-32"
                />
                <button onClick={send} disabled={busy || !text.trim()} className="h-9 px-3 rounded-xl bg-accent text-bg text-sm font-medium disabled:opacity-40">send</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
